import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    IconButton,
    TextField,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    CircularProgress,
} from '@mui/material';
import {
    Search as SearchIcon,
    Refresh as RefreshIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    Schedule as PendingIcon,
    PlayArrow as RunningIcon,
    Cancel as CancelledIcon,
    Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { WorkflowExecution, ExecutionStatus } from '../../types';

interface ExecutionsResponse {
    items: WorkflowExecution[];
}

const statusIcons = {
    [ExecutionStatus.COMPLETED]: <SuccessIcon color="success" />,
    [ExecutionStatus.FAILED]: <ErrorIcon color="error" />,
    [ExecutionStatus.PENDING]: <PendingIcon color="disabled" />,
    [ExecutionStatus.RUNNING]: <RunningIcon color="primary" />,
    [ExecutionStatus.CANCELLED]: <CancelledIcon color="warning" />,
};

const statusColors: Record<ExecutionStatus, 'success' | 'error' | 'default' | 'primary' | 'warning'> = {
    [ExecutionStatus.COMPLETED]: 'success',
    [ExecutionStatus.FAILED]: 'error',
    [ExecutionStatus.PENDING]: 'default',
    [ExecutionStatus.RUNNING]: 'primary',
    [ExecutionStatus.CANCELLED]: 'warning',
};

export default function ExecutionsList() {
    const navigate = useNavigate();
    const [executions, setExecutions] = useState<WorkflowExecution[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<ExecutionStatus | 'all'>('all');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchExecutions();
    }, []);

    const fetchExecutions = async () => {
        try {
            setLoading(true);
            
            // Get authentication token
            const { fetchAuthSession } = await import('aws-amplify/auth');
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();
            
            if (!idToken) {
                console.error('No authentication token available');
                return;
            }
            
            const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/executions`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const rawData = await response.json();

            // Handle both response formats (with or without 'items' property)
            let executionsData: WorkflowExecution[] = [];
            if (Array.isArray(rawData)) {
                executionsData = rawData as WorkflowExecution[];
            } else if (rawData.items && Array.isArray(rawData.items)) {
                executionsData = rawData.items as WorkflowExecution[];
            }

            setExecutions(executionsData);
        } catch (error) {
            console.error('Error fetching executions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const filteredExecutions = executions.filter((execution) => {
        const matchesSearch = execution.workflowId.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || execution.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString();
    };

    const calculateDuration = (startDate: string, endDate?: string) => {
        const start = new Date(startDate).getTime();
        const end = endDate ? new Date(endDate).getTime() : Date.now();
        const duration = end - start;

        const seconds = Math.floor(duration / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);

        if (hours > 0) {
            return `${hours}h ${minutes % 60}m`;
        } else if (minutes > 0) {
            return `${minutes}m ${seconds % 60}s`;
        } else {
            return `${seconds}s`;
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
                <Typography variant="h4" component="h1">
                    Workflow Executions
                </Typography>
                <IconButton onClick={fetchExecutions} color="primary">
                    <RefreshIcon />
                </IconButton>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <TextField
                    sx={{ flexGrow: 1 }}
                    variant="outlined"
                    placeholder="Search executions..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        value={statusFilter}
                        label="Status"
                        onChange={(e) => setStatusFilter(e.target.value as ExecutionStatus | 'all')}
                    >
                        <MenuItem value="all">All Statuses</MenuItem>
                        {Object.values(ExecutionStatus).map((status) => (
                            <MenuItem key={status} value={status}>
                                {status.charAt(0).toUpperCase() + status.slice(1)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Status</TableCell>
                            <TableCell>Workflow ID</TableCell>
                            <TableCell>Started</TableCell>
                            <TableCell>Duration</TableCell>
                            <TableCell>Input</TableCell>
                            <TableCell>Output/Error</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredExecutions
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((execution) => (
                                <TableRow
                                    key={execution.id}
                                    hover
                                    onClick={() => navigate(`/executions/${execution.id}`)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <TableCell>
                                        <Chip
                                            icon={statusIcons[execution.status]}
                                            label={execution.status}
                                            color={statusColors[execution.status]}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{execution.workflowId}</TableCell>
                                    <TableCell>{formatDate(execution.startedAt)}</TableCell>
                                    <TableCell>
                                        {calculateDuration(execution.startedAt, execution.completedAt)}
                                    </TableCell>
                                    <TableCell>
                                        {execution.input ? JSON.stringify(execution.input) : '-'}
                                    </TableCell>
                                    <TableCell>
                                        {execution.error
                                            ? <Typography color="error">{execution.error}</Typography>
                                            : execution.output
                                                ? JSON.stringify(execution.output)
                                                : '-'}
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredExecutions.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>
        </Box>
    );
}
